import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AiGovernance.css';
import AiGovNav from './AiGovNav';
import { getOrgPermissions } from '../../utils/permissions';
import { listAiAssets, getAssetReviewQueue, createAiAsset, retireAiAsset, reviewAiAsset } from './aiGovApi';

const ASSET_TYPES = ['AI_MODEL', 'AI_AGENT', 'AI_DATASET', 'VECTOR_STORE', 'LLM_ENDPOINT', 'FINETUNE_JOB', 'PROMPT_ASSET'];
const RISK_TIERS = ['UNACCEPTABLE', 'HIGH', 'LIMITED', 'MINIMAL'];
const SENSITIVITY = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const LIFECYCLE = ['PROPOSED', 'DEVELOPMENT', 'STAGING', 'PRODUCTION', 'RETIRED'];

const tierClass = (tier) =>
  tier === 'HIGH' || tier === 'UNACCEPTABLE' ? 'aig-b-high' : tier === 'LIMITED' ? 'aig-b-med' : 'aig-b-low';

export default function AiAssetInventory() {
  const { t } = useTranslation();
  const { canManageFrameworks } = getOrgPermissions();
  const [tab, setTab] = useState('inventory');
  const [assets, setAssets] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, rq] = await Promise.all([listAiAssets(), getAssetReviewQueue()]);
      setAssets(inv.content || []);
      setQueue(rq.content || []);
    } catch {
      /* surfaced via empty states */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const meta = (a, k) => (a.metadata && a.metadata[k]) || '';
  const highRisk = assets.filter((a) => ['HIGH', 'UNACCEPTABLE'].includes(meta(a, 'aiRiskTier'))).length;

  const onRetire = async (id) => { await retireAiAsset(id); load(); };
  const onReview = async (id, confirm) => { await reviewAiAsset(id, confirm); load(); };

  if (loading) return <div className="aig-page"><div className="aig-spinner" /></div>;

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('AIGovernance.assetsTitle', 'AI Asset Inventory')}</h1>
        <p className="aig-subtitle">{t('AIGovernance.assetsSubtitle', 'Models, agents, datasets, vector stores and LLM endpoints under governance.')}</p>
      </div>

      <AiGovNav />

      <div className="aig-stat-bar">
        <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.totalAiAssets', 'AI assets')}</div><div className="aig-stat-val">{assets.length}</div></div>
        <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.highRisk', 'High-risk (EU AI Act)')}</div><div className={`aig-stat-val ${highRisk ? 'aig-val-bad' : 'aig-val-ok'}`}>{highRisk}</div></div>
        <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.pendingReview', 'Pending review')}</div><div className={`aig-stat-val ${queue.length ? 'aig-val-warn' : 'aig-val-ok'}`}>{queue.length}</div></div>
      </div>

      <div className="aig-tabs">
        <button type="button" className={`aig-tab ${tab === 'inventory' ? 'aig-tab-active' : ''}`} onClick={() => setTab('inventory')}>{t('AIGovernance.inventory', 'Inventory')}</button>
        <button type="button" className={`aig-tab ${tab === 'review' ? 'aig-tab-active' : ''}`} onClick={() => setTab('review')}>
          {t('AIGovernance.reviewQueue', 'Review queue')}{queue.length ? ` (${queue.length})` : ''}
        </button>
      </div>

      <div className="aig-toolbar">
        <div className="aig-spacer" />
        {canManageFrameworks && tab === 'inventory' && (
          <button className="aig-btn aig-btn-primary" onClick={() => setShowAdd(true)}>+ {t('AIGovernance.addAsset', 'Register AI asset')}</button>
        )}
      </div>

      {tab === 'inventory' ? (
        assets.length === 0 ? (
          <div className="aig-empty">{t('AIGovernance.noAssets', 'No AI assets yet. Register one manually or run a connector discovery sync.')}</div>
        ) : (
          <table className="aig-table">
            <thead><tr>
              <th>{t('AIGovernance.name', 'Name')}</th><th>{t('AIGovernance.type', 'Type')}</th>
              <th>{t('AIGovernance.provider', 'Provider')}</th><th>{t('AIGovernance.riskTier', 'Risk tier')}</th>
              <th>{t('AIGovernance.sensitivity', 'Sensitivity')}</th><th>{t('AIGovernance.lifecycle', 'Lifecycle')}</th>
              <th>{t('AIGovernance.source', 'Source')}</th><th /></tr></thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id}>
                  <td>{a.displayName || a.name || a.externalId}</td>
                  <td>{a.assetType}</td>
                  <td>{meta(a, 'modelProvider') || a.cloudProvider || '—'}</td>
                  <td>{meta(a, 'aiRiskTier') ? <span className={`aig-badge ${tierClass(meta(a, 'aiRiskTier'))}`}>{meta(a, 'aiRiskTier')}</span> : <span className="aig-muted">—</span>}</td>
                  <td>{meta(a, 'dataSensitivity') || '—'}</td>
                  <td>{meta(a, 'lifecycleStage') || '—'}</td>
                  <td><span className="aig-badge aig-b-na">{a.source}</span></td>
                  <td>{canManageFrameworks && <button className="aig-btn" onClick={() => onRetire(a.id)}>{t('AIGovernance.retire', 'Retire')}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : queue.length === 0 ? (
        <div className="aig-empty">{t('AIGovernance.noReview', 'No discovered AI candidates awaiting review.')}</div>
      ) : (
        <table className="aig-table">
          <thead><tr><th>{t('AIGovernance.name', 'Name')}</th><th>{t('AIGovernance.type', 'Type')}</th><th>{t('AIGovernance.provider', 'Provider')}</th><th /></tr></thead>
          <tbody>
            {queue.map((a) => (
              <tr key={a.id}>
                <td>{a.displayName || a.name || a.externalId}</td>
                <td>{a.assetType}</td>
                <td>{a.cloudProvider || meta(a, 'modelProvider') || '—'}</td>
                <td style={{ display: 'flex', gap: '0.4rem' }}>
                  <button className="aig-btn aig-btn-primary" onClick={() => onReview(a.id, true)}>{t('AIGovernance.confirm', 'Confirm')}</button>
                  <button className="aig-btn" onClick={() => onReview(a.id, false)}>{t('AIGovernance.dismiss', 'Dismiss')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && <AddAssetModal t={t} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddAssetModal({ t, onClose, onSaved }) {
  const [form, setForm] = useState({
    assetType: 'AI_MODEL', name: '', modelProvider: '', aiRiskTier: 'LIMITED',
    dataSensitivity: 'INTERNAL', lifecycleStage: 'PRODUCTION', purpose: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await createAiAsset({
        assetType: form.assetType, layer: 'AI', name: form.name, displayName: form.name,
        metadata: {
          modelProvider: form.modelProvider, aiRiskTier: form.aiRiskTier,
          dataSensitivity: form.dataSensitivity, lifecycleStage: form.lifecycleStage, purpose: form.purpose,
        },
      });
      onSaved();
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className="aig-overlay" onClick={onClose}>
      <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('AIGovernance.addAsset', 'Register AI asset')}</h3>
        <div className="aig-field"><label>{t('AIGovernance.name', 'Name')}</label>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Customer support assistant" /></div>
        <div className="aig-field"><label>{t('AIGovernance.type', 'Type')}</label>
          <select value={form.assetType} onChange={(e) => set('assetType', e.target.value)}>{ASSET_TYPES.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div className="aig-field"><label>{t('AIGovernance.provider', 'Provider')}</label>
          <input value={form.modelProvider} onChange={(e) => set('modelProvider', e.target.value)} placeholder="OpenAI / Anthropic / self-hosted" /></div>
        <div className="aig-field"><label>{t('AIGovernance.riskTier', 'EU AI Act risk tier')}</label>
          <select value={form.aiRiskTier} onChange={(e) => set('aiRiskTier', e.target.value)}>{RISK_TIERS.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div className="aig-field"><label>{t('AIGovernance.sensitivity', 'Data sensitivity')}</label>
          <select value={form.dataSensitivity} onChange={(e) => set('dataSensitivity', e.target.value)}>{SENSITIVITY.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div className="aig-field"><label>{t('AIGovernance.lifecycle', 'Lifecycle stage')}</label>
          <select value={form.lifecycleStage} onChange={(e) => set('lifecycleStage', e.target.value)}>{LIFECYCLE.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div className="aig-field"><label>{t('AIGovernance.purpose', 'Purpose')}</label>
          <textarea rows={2} value={form.purpose} onChange={(e) => set('purpose', e.target.value)} /></div>
        <div className="aig-modal-actions">
          <button className="aig-btn" onClick={onClose}>{t('AIGovernance.cancel', 'Cancel')}</button>
          <button className="aig-btn aig-btn-primary" disabled={saving || !form.name} onClick={submit}>{t('AIGovernance.save', 'Save')}</button>
        </div>
      </div>
    </div>
  );
}
